import { type PaginatedResponse } from 'components/hooks/types'

import { type ComboboxFilterGroup, type ComboboxFilterOption } from '../../reusable/table/ComboboxMultiSelectFilter'
import { type Tag } from '../types'
import { type ManualReason, type MissingPaymentsRow } from './types'

export function getTagFilterOptions (
  tags: Tag[] | undefined
): ComboboxFilterGroup<string>[] {
  return Object.entries(
    (tags ?? []).reduce<{ temporary: Tag[]; permanent: Tag[] }>(
      (acc, tag) => ({
        ...acc,
        [tag.brokerage_id ? 'temporary' : 'permanent']: [
          ...acc[tag.brokerage_id ? 'temporary' : 'permanent'],
          tag,
        ],
      }),
      { temporary: [], permanent: [] }
    )
  )
    .map(([key, tags]) => ({
      groupLabel: key === 'temporary' ? 'Temporary Tags' : 'Permanent Tags',
      options: tags.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })),
    }))
    .sort((a) => (a.groupLabel === 'Permanent Tags' ? -1 : 1))
}

export function setMissingPaymentsQueryDataCallback (response: MissingPaymentsRow[]) {
  return (paginatedCacheData: PaginatedResponse<MissingPaymentsRow>) => {
    const cachedMissingPaymentsData = paginatedCacheData.data
    return {
      ...paginatedCacheData,
      data: cachedMissingPaymentsData.map(
        (navItem) =>
          response.find(
            (newNavItem) => newNavItem.id === navItem.id
          ) || navItem
      ),
    }
  }
}

type WithId = { id: string }

export function setQueryDataCallback<T extends WithId> (response: T[]) {
  return (paginatedCacheData: PaginatedResponse<T> | undefined) => {
    // If cache doesn't exist, return undefined to avoid creating invalid cache entry
    if (!paginatedCacheData?.data) {
      return paginatedCacheData
    }
    return {
      ...paginatedCacheData,
      data: paginatedCacheData.data.map(
        (item) => response.find((newItem) => newItem.id === item.id) || item
      ),
    }
  }
}

export function filterByLabelAndValueCallback (query: string) {
  return ({ label, value }: ComboboxFilterOption<string>) =>
    label?.toLowerCase().includes(query.toLowerCase()) ||
    value.toLowerCase().includes(query.toLowerCase())
}

export function filterByLabelAndExactValueCallback (query: string) {
  return ({ label, value }: ComboboxFilterOption<string>) =>
    label?.toLowerCase().includes(query.toLowerCase()) ||
    value.toLowerCase() === query.toLowerCase()
}

export function filterComboboxItems<T> (groupOptions: ComboboxFilterOption<T>[], selectedValues: T[]) {
  // Sort the options so that selected values are at the top
  // and excluded values are at the bottom.
  // If there are more than 6 options, don't sort since the UI has
  // plenty of space to display.
  return groupOptions.length > 6
    ? groupOptions.sort((a, b) => {
      const aSelected =
          selectedValues.includes(a.value)
            ? -1
            : 1
      const bSelected =
          selectedValues.includes(b.value)
            ? -1
            : 1
      return aSelected - bSelected
    })
    : groupOptions
}

export function toSnakeCase (str: string) {
  return str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

export const manualNAVItemTypes: ManualReason[] = [
  'incorrect_configuration',
  'payments_issue',
  'backdate_requested',
  'offboarding',
  'dependent_age',
  'other',
]
